<script lang="ts">
export default {
  name: "BasePanelHeading",
};
</script>

<script lang="ts" setup>
import {
  PanelHeadingShadowEnum,
  PanelHeadingTonesEnum,
} from "@/shared/components/base/Panel/Heading/types";
import Heading from "@/shared/components/base/Heading/index.vue";
import {
  HeadingLevelEnum,
  HeadingSizeEnum,
} from "@/shared/components/base/Heading/types";

type Props = {
  shadow?: `${PanelHeadingShadowEnum}`;
  level?: HeadingLevelEnum;
  size?: HeadingSizeEnum;
  tone?: `${PanelHeadingTonesEnum}`;
  // Reduced padding and type, for a heading on a `slim` panel.
  compact?: boolean;
  // A rule under the heading, so a slim panel's head still reads as a head
  // without the full frame's weight behind it.
  divider?: boolean;
};

const props = withDefaults(defineProps<Props>(), {
  shadow: undefined,
  level: HeadingLevelEnum.H1,
  size: HeadingSizeEnum.XXL,
  tone: PanelHeadingTonesEnum.DEFAULT,
  compact: false,
  divider: false,
});

const slots = defineSlots<{
  default?: () => unknown;
  subtitle?: () => unknown;
  actions?: () => unknown;
  // Something that belongs beside the title *and* its subtitle rather than
  // inside either -- an emblem, an avatar. `Heading` stacks the two into a
  // column, so a leading element has to sit outside it or it lands on the
  // title's line alone and the subtitle wraps underneath it.
  leading?: () => unknown;
}>();

const isMetric = computed(() => props.tone === PanelHeadingTonesEnum.METRIC);
</script>

<template>
  <div
    class="panel-heading"
    :class="{
      'panel-heading--top': shadow === 'top',
      'panel-heading--bottom': shadow === 'bottom',
      'panel-heading--metric': isMetric,
      'panel-heading--compact': compact,
      'panel-heading--divider': divider,
      'panel-heading--leading': slots.leading,
    }"
  >
    <div v-if="slots.leading" class="panel-heading__leading">
      <slot name="leading" />
    </div>

    <!--
      The metric tone is its own markup rather than a restyled Heading: the
      tracked uppercase Orbitron label is a span with a status dot, not a
      document heading, and running it through Heading would mean overriding
      every type property the component sets.
    -->
    <span v-if="isMetric" class="panel-heading__metric-title">
      <span class="panel-heading__dot" />
      <span data-test="panel-heading-title"><slot name="default" /></span>
    </span>

    <Heading
      v-else
      :level="level"
      :size="size"
      hero
      shadow
      class="panel-heading__title"
      data-test="panel-heading-title"
      :class="{
        'panel-heading__title--with-actions': slots.actions,
      }"
    >
      <template #default>
        <slot name="default" />
      </template>
      <template v-if="slots.subtitle" #subHeading>
        <slot name="subtitle" />
      </template>
    </Heading>

    <div v-if="slots.actions" class="panel-heading__actions">
      <slot name="actions" />
    </div>
  </div>
</template>

<style scoped>
@reference "../../../../../entrypoints/tailwind.css";

/*
 * Padding pairs with PanelBody's 4px 18px 18px. The old 15px 15px 0 closed the
 * heading flush, so every body under one opened with a redundant 15px of its
 * own - which is the whole reason `no-padding-top` was passed at 12 sites.
 */
.panel-heading {
  @apply relative flex justify-between text-text;
  padding: 16px 18px 12px;
}

/* A scrim for a heading sitting over a background image. Absorbs what the
   deleted PanelShadow component did from inside the panel body. */
.panel-heading--top {
  @apply items-start;
  border-top-left-radius: var(--radius-surface-inner, 14px);
  border-top-right-radius: var(--radius-surface-inner, 14px);
  /* Held through the middle rather than fading immediately: a heading that
     carries a subtitle as well as a title had nothing left under the second
     line, and a bright hull swallowed it. */
  background: linear-gradient(
    to bottom,
    rgb(0 0 0 / 0.8),
    rgb(0 0 0 / 0.55) 55%,
    transparent
  );
}

.panel-heading--bottom {
  @apply items-end;
  border-bottom-left-radius: var(--radius-surface-inner, 14px);
  border-bottom-right-radius: var(--radius-surface-inner, 14px);
  background: linear-gradient(to top, rgb(0 0 0 / 0.8), transparent);
}

.panel-heading__title {
  @apply w-full;
  padding: 0;
}

/*
 * With something beside it the title block can no longer be the full width, or
 * it pushes the leading element out of the box. `min-width: 0` as well, so a
 * long unbroken name truncates inside the column instead of widening it.
 *
 * Centred rather than topped: the leading element is an emblem the size of the
 * whole block, and a title on its own line hung level with the emblem's top
 * edge with the rest of the mark below it.
 */
.panel-heading--leading {
  @apply items-center;
  gap: 12px;
}

.panel-heading--leading .panel-heading__title {
  @apply w-auto min-w-0 flex-1;
}

.panel-heading__leading {
  @apply flex flex-none items-center;
}

/*
 * The title gives up exactly the width its actions take. A fixed reserve fit one
 * icon, and a second control - the public log beside add-to-hangar - sat on top
 * of the name.
 */
.panel-heading__title--with-actions {
  @apply w-auto min-w-0 flex-1;
}

/* A head can carry more than one control - the calendar's paginator sits beside
   its view switch - and they were separated by nothing but collapsed whitespace.
   Same 12px the metric head puts between its title and this block. The negative
   margins hold the controls 10px from the corner, closer in than the padding. */
.panel-heading__actions {
  @apply flex flex-none items-center self-start;
  gap: 12px;
  margin: -6px -8px 0 12px;
}

.panel-heading--compact {
  padding: 14px 16px;
}

.panel-heading--compact .panel-heading__actions {
  margin: -4px -6px 0 12px;
}

.panel-heading--divider {
  border-bottom: 1px solid var(--color-edge-soft, rgb(122 130 136 / 0.28));
  background: rgb(0 0 0 / 0.12);
}

/*
 * The metrics-card title: Orbitron, tracked, uppercase, with a gold status dot.
 * Kept to this tone rather than made the panel default - it works because a
 * metrics card's labels are short and fixed, and tracked uppercase across
 * free-form ship names breaks German compounds and CJK, which is the same call
 * the Btn redesign made about button labels.
 */
.panel-heading--metric {
  @apply items-center;
  gap: 12px;
  flex-wrap: wrap;
}

/*
 * Centred on the label rather than pinned to the top corner: a metric head is
 * one short line, so there is no taller block for the actions to top-align to.
 * The chips wrap within the card once they no longer fit beside the title -
 * the base card can carry five, which outrun a narrow column.
 */
.panel-heading--metric .panel-heading__actions {
  @apply min-w-0 shrink flex-wrap self-auto;
  row-gap: 8px;
  margin: 0;
}

/* The metric title sizes stay in px: the embed renders them inside other
   sites, where rem would follow the host page's root size, not ours. */
.panel-heading--compact .panel-heading__metric-title {
  font-size: 13px;
}

.panel-heading__metric-title {
  @apply flex items-center text-lifted;
  gap: 11px;
  font-family: "Orbitron", tahoma, sans-serif;
  font-size: 15px;
  letter-spacing: 0.2em;
  text-transform: uppercase;
}

.panel-heading__dot {
  @apply flex-none rounded-full bg-gold;
  width: 7px;
  height: 7px;
  box-shadow: 0 0 10px 1px rgb(212 175 55 / 0.55);
}
</style>
